import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  type SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { EntityOption } from '@/types/entity';

interface EntitySelectorProps {
  entities: EntityOption[];
  selectedEntityId: string;
  onChange: (entityId: string) => void;
  compact?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

export function EntitySelector({
  entities,
  selectedEntityId,
  onChange,
  compact = false,
  disabled = false,
  loading = false,
}: EntitySelectorProps) {
  const { t } = useTranslation('entity-portal');

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={37}
        sx={{
          borderRadius: 1.5,
          minWidth: compact ? 120 : { xs: 132, sm: 148 },
          maxWidth: compact ? 160 : 220,
          flex: 1,
        }}
      />
    );
  }

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: compact ? 120 : { xs: 132, sm: 148 },
        maxWidth: compact ? 160 : 220,
      }}
    >
      <Select
        value={
          entities.some((entity) => entity.id === selectedEntityId)
            ? selectedEntityId
            : ''
        }
        onChange={handleChange}
        displayEmpty
        disabled={disabled || entities.length === 0}
        IconComponent={KeyboardArrowDownIcon}
        aria-label={t('appHeader.appHeaderEntitySelectorLabel')}
        sx={{
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          color: 'text.primary',
          typography: 'subtitle1',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'text.disabled',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
          '& .MuiSelect-select': {
            py: 0.75,
            pl: 1.25,
            pr: '28px !important',
          },
          '& .MuiSelect-icon': {
            fontSize: 18,
            color: 'text.secondary',
            right: 8,
          },
        }}
      >
        {entities.map((entity) => (
          <MenuItem key={entity.id} value={entity.id}>
            {entity.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
